import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Dashboard } from './components/Dashboard';
import { Navigation } from './components/Navigation';
import { Login } from './components/Login';
import { Register } from './components/Register';
import { useCustomerData } from './hooks/useCustomerData';
import { useUserSettings } from './hooks/useUserSettings';
import { useAuth } from './hooks/useAuth';
import { Customer } from './types';
import { setupVisibilityListener, ensureValidSession } from './utils/sessionManager';

const CustomerList = lazy(() => import('./components/CustomerList').then(m => ({ default: m.CustomerList })));
const CustomerForm = lazy(() => import('./components/CustomerForm').then(m => ({ default: m.CustomerForm })));
const TimePurchaseForm = lazy(() => import('./components/TimePurchaseForm').then(m => ({ default: m.TimePurchaseForm })));
const TimeRegistration = lazy(() => import('./components/TimeRegistration').then(m => ({ default: m.TimeRegistration })));
const AssignmentManager = lazy(() => import('./components/AssignmentManager').then(m => ({ default: m.AssignmentManager })));
const Reports = lazy(() => import('./components/Reports').then(m => ({ default: m.Reports })));
const MigrationRunner = lazy(() => import('./components/MigrationRunner').then(m => ({ default: m.MigrationRunner })));
const Settings = lazy(() => import('./components/Settings').then(m => ({ default: m.Settings })));
const TravelJournal = lazy(() => import('./components/TravelJournal').then(m => ({ default: m.TravelJournal })));
const UnbilledReport = lazy(() => import('./components/UnbilledReport').then(m => ({ default: m.UnbilledReport })));
const ArticleRegistry = lazy(() => import('./components/ArticleRegistry').then(m => ({ default: m.ArticleRegistry })));
const ArticlePackages = lazy(() => import('./components/ArticlePackages').then(m => ({ default: m.ArticlePackages })));
const HardwareRegistry = lazy(() => import('./components/HardwareRegistry').then(m => ({ default: m.HardwareRegistry })));
const HardwareLinkingAssistant = lazy(() => import('./components/HardwareLinkingAssistant').then(m => ({ default: m.HardwareLinkingAssistant })));
const AssignmentExport = lazy(() => import('./components/AssignmentExport').then(m => ({ default: m.AssignmentExport })));
const TimebankPurchaseExport = lazy(() => import('./components/TimebankPurchaseExport').then(m => ({ default: m.TimebankPurchaseExport })));

const LoadingFallback = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Laddar...</p>
    </div>
  </div>
);

function App() {
  const [currentView, setCurrentView] = useState('dashboard');
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [initialCustomerName, setInitialCustomerName] = useState<string>('');
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [selectedCustomerForPurchase, setSelectedCustomerForPurchase] = useState<Customer | null>(null);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedHardwareId, setSelectedHardwareId] = useState<string | null>(null);

  const { user, userRole, loading: authLoading, signOut, isAdmin, isEmployee, canSeeAllCustomers } = useAuth();

  const {
    customers,
    purchases,
    transactions,
    assignments,
    timeEntries,
    loading: dataLoading,
    hasMissingTablesError,
    isOnline,
    loadData,
    addAssignment,
    updateAssignment,
    addTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
    deleteAssignment,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addPurchase
  } = useCustomerData(user?.id || null);

  const { settings: userSettings, updateSettings: updateUserSettings } = useUserSettings(user?.id || null);

  const handleAddAssignment = async (assignment: any) => {
    const result = await addAssignment(assignment);
    if (result) {
      setCurrentView('dashboard');
    }
    return result;
  };

  // Setup session management
  useEffect(() => {
    if (!user) return;

    // Ensure valid session when component mounts
    ensureValidSession();

    // Setup visibility listener to refresh session when user returns to tab
    const cleanup = setupVisibilityListener();

    // Setup periodic session check (every 5 minutes)
    const intervalId = setInterval(() => {
      ensureValidSession();
    }, 5 * 60 * 1000);

    return () => {
      if (cleanup) cleanup();
      clearInterval(intervalId);
    };
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Laddar...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    if (showRegisterForm) {
      return <Register onSwitchToLogin={() => setShowRegisterForm(false)} />;
    }
    return <Login onLoginSuccess={() => {}} onSwitchToRegister={() => setShowRegisterForm(true)} />;
  }

  // Show migration runner if tables are missing
  if (hasMissingTablesError) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <MigrationRunner onMigrationComplete={() => window.location.reload()} />
      </Suspense>
    );
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <Dashboard
            customers={customers}
            timeEntries={timeEntries}
            assignments={assignments}
            userSettings={userSettings}
            onUpdateUserSettings={updateUserSettings}
            onNavigateToRegister={() => setCurrentView('register')}
            onNavigateToCustomers={() => setCurrentView('customers')}
            onNavigateToHardware={() => setCurrentView('hardware')}
            onShowNewCustomerForm={(prefillName?: string) => {
              setCurrentView('customers');
              setInitialCustomerName(prefillName || '');
              setShowCustomerForm(true);
            }}
            onNavigateToCustomerWithHardware={(customerId: string, hardwareId: string) => {
              setSelectedCustomerId(customerId);
              setSelectedHardwareId(hardwareId);
              setCurrentView('customers');
            }}
            onUpdateTimeEntry={updateTimeEntry}
            onDeleteTimeEntry={deleteTimeEntry}
            onUpdateAssignment={updateAssignment}
            onAddAssignment={handleAddAssignment}
            onRefreshCustomers={loadData}
          />
        );
      case 'customers':
        if (!canSeeAllCustomers) {
          return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <p className="text-yellow-800">Du har inte behörighet att visa kundlistan</p>
            </div>
          );
        }
        return (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-4">
              <button
                onClick={() => setShowCustomerForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                Lägg till Ny Kund
              </button>
            </div>
            <CustomerList
              customers={customers}
              assignments={assignments}
              purchases={purchases}
              onUpdateCustomer={updateCustomer}
              onDeleteCustomer={deleteCustomer}
              onOpenPurchaseForm={(customerId) => {
                const customer = customers.find(c => c.id === customerId);
                if (customer) {
                  setSelectedCustomerForPurchase(customer);
                  setShowPurchaseForm(true);
                }
              }}
              onNavigateToReports={(customerId) => {
                localStorage.setItem('selected_customer_for_report', customerId);
                setCurrentView('reports');
              }}
              onUpdateAssignment={updateAssignment}
              onDeleteAssignment={deleteAssignment}
              onAddAssignment={handleAddAssignment}
              onNavigateToTimeRegistration={(assignmentId) => {
                localStorage.setItem('preselected_assignment_id', assignmentId);
                setCurrentView('register');
              }}
              selectedCustomerId={selectedCustomerId}
              selectedHardwareId={selectedHardwareId}
              onClearSelection={() => {
                setSelectedCustomerId(null);
                setSelectedHardwareId(null);
              }}
            />
          </div>
        );
      case 'assignments':
        return (
          <AssignmentManager
              customers={customers}
              assignments={assignments}
              timeEntries={timeEntries}
              userSettings={userSettings}
              onUpdateUserSettings={updateUserSettings}
              onAddAssignment={handleAddAssignment}
              onUpdateAssignment={updateAssignment}
              onDeleteAssignment={deleteAssignment}
              onUpdateTimeEntry={updateTimeEntry}
              onDeleteTimeEntry={deleteTimeEntry}
            />
        );
      case 'register':
        return (
          <TimeRegistration
            customers={customers}
            assignments={assignments}
            timeEntries={timeEntries}
            userSettings={userSettings}
            onAddTimeEntry={addTimeEntry}
            onAddAssignment={handleAddAssignment}
            onUpdateAssignment={updateAssignment}
            onNavigate={setCurrentView}
          />
        );
      case 'reports':
        return (
          <Reports
            userId={user?.id || ''}
            customers={customers}
            assignments={assignments}
            timeEntries={timeEntries}
            purchases={purchases}
            onUpdateTimeEntry={updateTimeEntry}
            onDeleteTimeEntry={deleteTimeEntry}
            onRefresh={loadData}
            initialTab="customer"
          />
        );
      case 'distance-report':
        return (
          <Reports
            userId={user?.id || ''}
            customers={customers}
            assignments={assignments}
            timeEntries={timeEntries}
            purchases={purchases}
            onUpdateTimeEntry={updateTimeEntry}
            onDeleteTimeEntry={deleteTimeEntry}
            onRefresh={loadData}
            initialTab="distance"
          />
        );
      case 'completion-report':
        return (
          <Reports
            userId={user?.id || ''}
            customers={customers}
            assignments={assignments}
            timeEntries={timeEntries}
            purchases={purchases}
            onUpdateTimeEntry={updateTimeEntry}
            onDeleteTimeEntry={deleteTimeEntry}
            onRefresh={loadData}
            initialTab="completion"
          />
        );
      case 'monthly-report':
        return (
          <Reports
            userId={user?.id || ''}
            customers={customers}
            assignments={assignments}
            timeEntries={timeEntries}
            purchases={purchases}
            onUpdateTimeEntry={updateTimeEntry}
            onDeleteTimeEntry={deleteTimeEntry}
            onRefresh={loadData}
            initialTab="monthly"
          />
        );
      case 'settings':
        return <Settings userId={user?.id || ''} onNavigate={setCurrentView} />;
      case 'articles':
        return <ArticleRegistry />;
      case 'packages':
        return <ArticlePackages />;
      case 'hardware':
        return <HardwareRegistry />;
      case 'hardware-linking':
        return <HardwareLinkingAssistant />;
      case 'travel-journal':
        return <TravelJournal />;
      case 'unbilled':
        return <UnbilledReport />;
      case 'assignment-export':
        return (
          <AssignmentExport
            customers={customers}
            assignments={assignments}
            timeEntries={timeEntries}
          />
        );
      case 'timebank-purchase-export':
        return (
          <TimebankPurchaseExport
            customers={customers}
          />
        );
      default:
        return (
          <Dashboard
            customers={customers}
            timeEntries={timeEntries}
            assignments={assignments}
            userSettings={userSettings}
            onUpdateUserSettings={updateUserSettings}
            onNavigateToRegister={() => setCurrentView('register')}
            onNavigateToCustomers={() => setCurrentView('customers')}
            onNavigateToHardware={() => setCurrentView('hardware')}
            onShowNewCustomerForm={(prefillName?: string) => {
              setCurrentView('customers');
              setInitialCustomerName(prefillName || '');
              setShowCustomerForm(true);
            }}
            onNavigateToCustomerWithHardware={(customerId: string, hardwareId: string) => {
              setSelectedCustomerId(customerId);
              setSelectedHardwareId(hardwareId);
              setCurrentView('customers');
            }}
            onUpdateTimeEntry={updateTimeEntry}
            onDeleteTimeEntry={deleteTimeEntry}
            onUpdateAssignment={updateAssignment}
            onAddAssignment={handleAddAssignment}
            onRefreshCustomers={loadData}
          />
        );
    }
  };

  return (
    <>
      <Navigation
        currentView={currentView}
        onViewChange={setCurrentView}
        onLogout={signOut}
        user={user}
        userRole={userRole}
      />
      <main className="min-h-screen bg-gray-50 pt-20 pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {!isOnline && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Ingen internetanslutning. Funktionalitet kan vara begränsad.
                </p>
              </div>
            </div>
          </div>
        )}
        {dataLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Laddar data...</p>
          </div>
        ) : (
          <Suspense fallback={<LoadingFallback />}>
            {renderCurrentView()}
          </Suspense>
        )}
        </div>
      </main>

      <Suspense fallback={null}>
        {showCustomerForm && (
          <CustomerForm
            onAddCustomer={async (customer) => {
              await addCustomer(customer);
              setShowCustomerForm(false);
              setInitialCustomerName('');
            }}
            onClose={() => {
              setShowCustomerForm(false);
              setInitialCustomerName('');
            }}
            initialName={initialCustomerName}
            customers={customers}
          />
        )}

        {showPurchaseForm && selectedCustomerForPurchase && (
          <TimePurchaseForm
            customer={selectedCustomerForPurchase}
            onAddPurchase={async (purchase) => {
              await addPurchase(purchase);
              setShowPurchaseForm(false);
              setSelectedCustomerForPurchase(null);
            }}
            onClose={() => {
              setShowPurchaseForm(false);
              setSelectedCustomerForPurchase(null);
            }}
          />
        )}
      </Suspense>
    </>
  );
}

export default App;